import { ERROR_MESSAGES, type ApiErrorBody, type ErrorCode } from '@yana/shared';
import { beginApiActivity } from './activity';

/**
 * HTTP-клиент.
 *
 * Все запросы идут на тот же origin (в dev — через прокси Vite), поэтому
 * cookie сессии уезжает сама и возни с CORS и SameSite не возникает.
 */

const BASE = '/api/v1';

/** Ошибка, о которой можно осмысленно сообщить пользователю. */
export class ApiError extends Error {
  readonly code: ErrorCode | string;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message || ERROR_MESSAGES.INTERNAL_ERROR);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.fields = body.fields;
    this.details = body.details;
    this.requestId = body.requestId;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** Вызывается при 401 — роутер уводит на экран входа. */
let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

/** HTTP и WebSocket используют один переход на экран входа. */
export function notifyUnauthenticated(): void {
  onUnauthenticated?.();
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const completed = beginApiActivity(path, init);
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 204) { completed(undefined); return undefined as T; }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const errBody = (body as ApiErrorBody)?.error ?? {
      code: 'INTERNAL_ERROR' as ErrorCode,
      message: typeof body === 'string' && body ? body : 'Сервер не ответил',
    };

    if (res.status === 401) notifyUnauthenticated();

    throw new ApiError(res.status, errBody);
  }

  completed(body);
  return body as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),

  post: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) }),

  patch: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'PATCH',
      body: data === undefined ? undefined : JSON.stringify(data),
    }),

  put: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', body: data === undefined ? undefined : JSON.stringify(data) }),

  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
};

/** Идентификатор клиентской операции: идемпотентность + узнавание своего эха в realtime. */
const localOperations = new Set<string>();

export function isOwnOperation(id: string | null | undefined): boolean {
  return !!id && localOperations.has(id);
}

export function newOpId(): string {
  const id = crypto.randomUUID();
  localOperations.add(id);
  if (localOperations.size > 2000) localOperations.delete(localOperations.values().next().value!);
  return id;
}
